import React, { createContext, ReactNode, useContext, useState } from 'react';
import {
  ContentType,
  GeneratedPlan,
  GeneratedScript,
  GenerateScriptParams,
} from 'src/api/generatedContent';

interface AIAssistantContextType {
  // Tab navigation
  activeTab: number;
  switchToTab: (index: number) => void;

  // Content type selection
  contentType: ContentType;
  setContentType: (type: ContentType) => void;

  // Circle plan state
  circlePlan: GeneratedPlan | null;
  setCirclePlan: (plan: GeneratedPlan | null) => void;
  selectedChallengeIndex: number;
  setSelectedChallengeIndex: (index: number) => void;

  // Prefilled data for developing an Challenge
  prefilledScriptData: GenerateScriptParams | null;
  setPrefilledScriptData: (data: GenerateScriptParams | null) => void;

  // Video Script state
  script: GeneratedScript | null;
  setScript: (script: GeneratedScript | null) => void;
  isContentGenerated: boolean;
  setIsContentGenerated: (generated: boolean) => void;

  // Reset form states
  resetFormStates: () => void;
}

// Create the context with default values
const AIAssistantContext = createContext<AIAssistantContextType | undefined>(
  undefined
);

interface AIAssistantProviderProps {
  children: ReactNode;
}

// Provider component
export const AIAssistantProvider: React.FC<AIAssistantProviderProps> = ({
  children,
}) => {
  // Tab navigation
  const [activeTab, setActiveTab] = useState<number>(0);

  // Content type selection
  const [contentType, setContentType] = useState<ContentType>(
    ContentType.CIRCLE_PLAN
  );

  // Circle plan state
  const [circlePlan, setCirclePlan] = useState<GeneratedPlan | null>(null);
  const [selectedChallengeIndex, setSelectedChallengeIndex] =
    useState<number>(-1);

  // Prefilled data for developing an Challenge
  const [prefilledScriptData, setPrefilledScriptData] =
    useState<GenerateScriptParams | null>(null);

  // Generated content state
  const [script, setScript] = useState<GeneratedScript | null>(null);

  const [isContentGenerated, setIsContentGenerated] = useState<boolean>(false);

  // Function to switch tabs
  const switchToTab = (index: number) => {
    if (index === 0 && activeTab === 1) {
      // console.log('Switching back to Create tab, checking if reset needed...');
      if (!circlePlan && !script && isContentGenerated) {
        // console.log(
        //   'No content but isContentGenerated=true, resetting form states'
        // );
        resetFormStates();
      }
    }

    setActiveTab(index);
  };

  // Function to reset form states
  const resetFormStates = () => {
    setScript(null);
    setCirclePlan(null);
    setSelectedChallengeIndex(-1);
    setIsContentGenerated(false);
    setPrefilledScriptData(null);
    setContentType(ContentType.CIRCLE_PLAN);
  };

  // Context value
  const value: AIAssistantContextType = {
    activeTab,
    switchToTab,

    contentType,
    setContentType,

    circlePlan,
    setCirclePlan,
    selectedChallengeIndex,
    setSelectedChallengeIndex,

    prefilledScriptData,
    setPrefilledScriptData,

    script,
    setScript,

    isContentGenerated,
    setIsContentGenerated,

    resetFormStates,
  };

  return (
    <AIAssistantContext.Provider value={value}>
      {children}
    </AIAssistantContext.Provider>
  );
};

// custom hook to use the context
export const useAIAssistantContext = () => {
  const context = useContext(AIAssistantContext);

  if (context === undefined) {
    throw new Error(
      'useAIAssistantContext must be used within an AIAssistantProvider'
    );
  }

  return context;
};
